import React, { useState, useMemo, useEffect } from "react";
import { safeFormat } from "../../../../utils/financialHelper";
import "./financial.less";
import { Reit, YearData, FinancialData } from "../../../../types/reit.types";

interface FinancialProps {
  reit: Reit | null;
}

interface FinancialRow {
  metric: string;
  values: (string | number)[];
}

const Financial: React.FC<FinancialProps> = ({ reit }) => {
  const [view, setView] = useState<"Yearly" | "Quarterly">("Yearly");
  const [year, setYear] = useState<string>("");

  const years = useMemo(() => {
    if (!reit?.years) return [];
    return reit.years.map((y: YearData) => `FY ${y.year}`);
  }, [reit]);

  useEffect(() => {
    if (years.length && !year) {
      setYear(years[0]);
    }
  }, [years, year]);

  const financialRows: FinancialRow[] = useMemo(() => {
    if (!reit?.years?.length || !year) return [];

    const yearValue = year.replace("FY ", "");
    const yearData = reit.years.find((y) => y.year === yearValue);

    if (!yearData) return [];

    if (view === "Yearly") {
      const data: FinancialData = yearData.financialData;
      return Object.keys(data).map((key) => ({
        metric: data[key as keyof FinancialData]?.key ?? key,
        values: [data[key as keyof FinancialData]?.value ?? "---"],
      }));
    } else {
      const quarters = (yearData as any).quarters || [];
      if (!quarters.length) return [];

      const keys = Object.keys(
        (quarters[0]?.financialData as FinancialData) || {}
      );

      return keys.map((key) => ({
        metric:
          quarters[0]?.financialData?.[key as keyof FinancialData]?.key ?? key,
        values: quarters.map(
          (q: { financialData: FinancialData }) =>
            q.financialData?.[key as keyof FinancialData]?.value ?? "---"
        ),
      }));
    }
  }, [reit, year, view]);

  return (
    <section className="financial">
      <div className="financial__card">
        {/* Header */}
        <div className="financial__header">
          <h2>Key Financial Performance</h2>

          <div className="financial__subheader">
            {/* Toggle */}
            <div className="financial__tabs">
              <button
                className={view === "Yearly" ? "active" : ""}
                onClick={() => setView("Yearly")}
              >
                Yearly
              </button>
              <button
                className={view === "Quarterly" ? "active" : ""}
                onClick={() => setView("Quarterly")}
              >
                Quarterly
              </button>
            </div>

            {/* Dropdown */}
            <div className="financial__dropdown">
              <div className="custom-select">
                <select value={year} onChange={(e) => setYear(e.target.value)}>
                  {years.map((y, idx) => (
                    <option key={idx} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="financial__table">
          <table>
            <thead>
              <tr>
                <th>Metric</th>
                {view === "Quarterly" ? (
                  <>
                    <th>Q1</th>
                    <th>Q2</th>
                    <th>Q3</th>
                    <th>Q4</th>
                  </>
                ) : (
                  <th>Value</th>
                )}
              </tr>
            </thead>
            <tbody>
              {financialRows.map((row, idx) => (
                <tr key={idx}>
                  <td
                    className={
                      row.metric.startsWith("-")
                        ? "child-metric"
                        : "parent-metric"
                    }
                  >
                    {row.metric.startsWith("-")
                      ? row.metric.replace("-", "- ")
                      : row.metric}
                  </td>

                  {row.values.map((val, i) => (
                    <td key={i}>
                      {typeof val === "number" ? safeFormat(val) : val}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default Financial;
